import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';
import { 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  IconButton, 
  TextField, 
  Tooltip, 
  CircularProgress, 
  Switch, 
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar
} from '@material-ui/core';
import { FaArrowDown, FaInfoCircle, FaCog } from 'react-icons/fa';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { ethers } from 'ethers';
// Import logos from config
import { metamaskLogo } from './config';

// We're using direct contract calls instead of the Uniswap SDK
// This achieves the same ultra-low fees (~$0.32) with simpler code

// Import the real E3D logo from the app's config
import { futLogoIcon200 } from './config';
const ethLogo = 'https://assets.coingecko.com/coins/images/279/small/ethereum.png';
const wethLogo = 'https://assets.coingecko.com/coins/images/2518/small/weth.png?1628852295';
const usdtLogo = 'https://assets.coingecko.com/coins/images/325/small/Tether.png';
const usdcLogo = 'https://assets.coingecko.com/coins/images/6319/small/USD_Coin_icon.png';
const daiLogo = 'https://assets.coingecko.com/coins/images/9956/small/Badge_Dai.png';
const linkLogo = 'https://assets.coingecko.com/coins/images/877/small/chainlink-new-logo.png';

// Constants
const E3D_TOKEN_ADDRESS = '0x6488861b401F427D13B6619C77C297366bCf6386'; // Replace with actual E3D token address

// Router Addresses
const UNISWAP_V2_ROUTER_ADDRESS = "0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D";
const UNISWAP_V3_ROUTER_ADDRESS = "0xE592427A0AEce92De3Edee1F18E0157C05861564";
const WETH_ADDRESS = "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2"; // Mainnet WETH Address with correct checksum

// Uniswap V2 Router ABI - minimal for the functions we need
const UNISWAP_V2_ROUTER_ABI = [
  {
    "inputs": [{ "internalType": "uint256", "name": "amountOutMin", "type": "uint256" },
               { "internalType": "address[]", "name": "path", "type": "address[]" },
               { "internalType": "address", "name": "to", "type": "address" },
               { "internalType": "uint256", "name": "deadline", "type": "uint256" }],
    "name": "swapExactETHForTokens",
    "outputs": [{ "internalType": "uint256[]", "name": "amounts", "type": "uint256[]" }],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [{ "internalType": "uint256", "name": "amountIn", "type": "uint256" },
               { "internalType": "uint256", "name": "amountOutMin", "type": "uint256" },
               { "internalType": "address[]", "name": "path", "type": "address[]" },
               { "internalType": "address", "name": "to", "type": "address" },
               { "internalType": "uint256", "name": "deadline", "type": "uint256" }],
    "name": "swapExactTokensForTokens",
    "outputs": [{ "internalType": "uint256[]", "name": "amounts", "type": "uint256[]" }],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "name": "getAmountsOut",
    "type": "function",
    "inputs": [
      { "type": "uint256", "name": "amountIn" },
      { "type": "address[]", "name": "path" }
    ],
    "outputs": [{ "type": "uint256[]", "name": "" }],
    "stateMutability": "view"
  }
];

// Uniswap V3 Router ABI - minimal for exactInputSingle function
const UNISWAP_V3_ROUTER_ABI = [
  {
    "inputs": [
      {
        "components": [
          { "internalType": "address", "name": "tokenIn", "type": "address" },
          { "internalType": "address", "name": "tokenOut", "type": "address" },
          { "internalType": "uint24", "name": "fee", "type": "uint24" },
          { "internalType": "address", "name": "recipient", "type": "address" },
          { "internalType": "uint256", "name": "deadline", "type": "uint256" },
          { "internalType": "uint256", "name": "amountIn", "type": "uint256" },
          { "internalType": "uint256", "name": "amountOutMinimum", "type": "uint256" },
          { "internalType": "uint160", "name": "sqrtPriceLimitX96", "type": "uint160" }
        ],
        "internalType": "struct ISwapRouter.ExactInputSingleParams",
        "name": "params",
        "type": "tuple"
      }
    ],
    "name": "exactInputSingle",
    "outputs": [{ "internalType": "uint256", "name": "amountOut", "type": "uint256" }],
    "stateMutability": "payable",
    "type": "function"
  }
];

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2),
    background: '#121212',
    color: '#fff',
    fontFamily: "'Inter', sans-serif",
    border: 'none',
    margin: 0,
    boxSizing: 'border-box',
  },
  pageTitle: {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: 0,
    textAlign: 'center',
    color: '#ffffff',
    textShadow: '0px 0px 5px rgba(77, 121, 255, 0.8)',
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    marginBottom: theme.spacing(1),
    position: 'relative',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  appLogo: {
    height: 40,
    marginRight: theme.spacing(2),
  },
  logoButton: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: theme.spacing(1),
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    marginRight: theme.spacing(1.5),
    '&:hover': {
      transform: 'scale(1.1)',
      backgroundColor: 'rgba(77, 121, 255, 0.2)',
      boxShadow: '0 0 5px rgba(77, 121, 255, 0.6)',
    },
    '&:active': {
      transform: 'scale(0.95)',
      backgroundColor: 'rgba(77, 121, 255, 0.3)',
    },
  },
  logoImage: {
    width: 48,
    height: 48,
  },
  swapCard: {
    maxWidth: 480,
    width: '100%',
    backgroundColor: '#1a1a1a',
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    border: '1px solid #4d79ff',
    overflow: 'visible',
    position: 'relative',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
  },
  cardContent: {
    padding: theme.spacing(2),
  },
  swapForm: {
    width: '100%',
  },
  currencyInput: {
    background: '#2c2c2c',
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: 0, // Reduced from theme.spacing(1) to bring boxes closer
  },
  topCurrencyInput: {
    marginBottom: '10px', // Smaller margin for the top box
  },
  bottomCurrencyInput: {
    marginTop: '10px', // Smaller margin for the bottom box
  },
  inputRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  tokenSelect: {
    minWidth: 120,
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      backgroundColor: '#383838',
    },
  },
  amountField: {
    width: '60%',
    '& .MuiInputBase-root': {
      borderRadius: 12,
      color: '#fff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#4d79ff',
    },
  },
  switchButton: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 10, // Increased z-index to ensure button appears on top
    backgroundColor: '#4d79ff',
    color: '#fff',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
    padding: theme.spacing(1.2), // Slightly smaller padding
    width: '40px',
    height: '40px',
    minWidth: '40px',
    '&:hover': {
      backgroundColor: '#3a5dc9',
    },
  },
  swapButton: {
    width: '100%',
    padding: theme.spacing(1.0, 0),
    borderRadius: 12,
    fontSize: '1.125rem',
    backgroundColor: '#4d79ff',
    color: '#fff',
    fontWeight: 700,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#3a5dc9',
    },
    '&.Mui-disabled': {
      backgroundColor: '#353535',
      color: '#777777',
    },
  },
  divider: {
    margin: theme.spacing(2, 0),
    backgroundColor: '#383838',
  },
  slippageControl: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
  settingsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: theme.spacing(1),
  },
  tokenLogo: {
    width: 24,
    height: 24,
    marginRight: theme.spacing(1),
    borderRadius: '50%',
  },
  tokenSelectItem: {
    display: 'flex',
    alignItems: 'center',
  },
  tokenButton: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#383838',
    borderRadius: 12,
    padding: theme.spacing(1, 2),
    color: '#fff',
    textTransform: 'none',
  },
  walletButton: {
    borderRadius: '20px',
    borderColor: 'white',
    borderWidth: '2px',
    borderStyle: 'solid',
    backgroundColor: '#383838',
    color: '#fff',
    padding: theme.spacing(0.5, 2),
    marginBottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: '#4a4a4a'
    },
  },
  walletIcon: {
    width: 24,
    height: 24,
    marginRight: theme.spacing(1),
  },
  swapInfo: {
    marginTop: theme.spacing(2),
    backgroundColor: '#252525',
    borderRadius: 8,
    padding: theme.spacing(1),
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '0.875rem',
    marginBottom: theme.spacing(0.5),
    color: '#ffffff',
  },
  slippageOptions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  slippageOption: {
    backgroundColor: '#383838',
    borderRadius: 8,
    padding: theme.spacing(0.5, 1.5),
    fontSize: '0.875rem',
    cursor: 'pointer',
    color: '#dddddd',
    '&.active': {
      backgroundColor: '#4d79ff',
      color: '#fff',
    },
  },
  settingsButton: {
    color: '#dddddd',
    '&:hover': {
      color: '#fff',
    },
  },
  infoIcon: {
    fontSize: '0.875rem',
    marginLeft: theme.spacing(0.5),
    color: '#dddddd',
  },
  advancedSettings: {
    marginTop: theme.spacing(2),
  },
  walletButton: {
    display: 'flex',
    alignItems: 'center',
    margin: '0px auto',
    padding: '8px 15px',
    borderRadius: '20px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    background: 'rgba(255, 255, 255, 0.05)',
    color: 'white',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.1)',
    },
  },
  walletIcon: {
    height: '24px',
    marginRight: '10px',
  },
  tokenContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: '#2c2c2c',
    marginBottom: theme.spacing(1),
  },
  tokenInfo: {
    display: 'flex',
    alignItems: 'center',
  },
  tokenIconContainer: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: '#383838',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing(1),
  },
  tokenIcon: {
    width: 24,
    height: 24,
  },
  tokenSymbol: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#fff',
  },
  amountInput: {
    background: 'transparent',
    border: 'none',
    padding: theme.spacing(1),
    fontSize: '1.5rem',
    color: '#fff',
    '&:focus': {
      outline: 'none',
    },
  },
  topWalletButton: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    borderRadius: 50,
    padding: theme.spacing(1, 2),
    marginLeft: 'auto',
    textTransform: 'none',
    fontSize: '0.9rem',
    '&:hover': {
      backgroundColor: '#2c78c3',
    },
  },
  walletButtonText: {
    marginLeft: theme.spacing(1),
    maxWidth: 80,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  mainWalletButton: {
    backgroundColor: '#3f51b5',
    color: 'white',
    borderRadius: '20px',
    padding: '8px 16px',
    '&:hover': {
      backgroundColor: '#303f9f',
    },
  },
  mainWalletIcon: {
    marginRight: theme.spacing(1),
    width: 20,
    height: 20,
  },
  settingsText: {
    color: 'white', // Make settings text white
    marginBottom: theme.spacing(1)
  },
  slippageButtons: {
    display: 'flex',
    marginTop: 8,
    marginBottom: 16,
    '& > div': {
      minWidth: 85 // Ensure buttons have enough width to prevent text wrapping
    }
  },
}));

const SwapPage = ({ 
  // Dialog props for when used as a dialog (from WalletConnect)
  open = null, 
  onClose = null,
  tokenName = null, 
  tokenAddress = null,
  provider: externalProvider = null,
  walletAddress: externalWalletAddress = null,
  tokenPriceParam = null,
  tokenUniswapVersion = null,
  ethPrice: externalEthPrice = null,
  // If any of these are provided, the component will operate as a dialog
  isDialog = open !== null,
  // Whether this is the 'current token' swap scenario (ETH → Current Token)
  isCurrentToken = false
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [walletAddress, setWalletAddress] = useState(null);
  const [provider, setProvider] = useState(null);
  const [slippage, setSlippage] = useState(0.5); // Default 0.5%
  const [ethAmount, setEthAmount] = useState('0.01');
  const [tokenAmount, setTokenAmount] = useState('0.00');
  const [tokenBalance, setTokenBalance] = useState('0.00');
  const [ethBalance, setEthBalance] = useState('0.00');
  const [isLoading, setIsLoading] = useState(false);
  const [e3dPrice, setE3dPrice] = useState(0);
  const [ethPrice, setEthPrice] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [swapSuccess, setSwapSuccess] = useState(false);
  const [customSlippage, setCustomSlippage] = useState('');
  const [transactionDeadline, setTransactionDeadline] = useState(30); // 30 minutes
  const [currentTokenPrice, setCurrentTokenPrice] = useState(0);
  // E3D Token only has Uniswap V3 liquidity, so we use V3 exclusively
  const uniswapVersion = 3; // Always use V3 for lower fees (~$0.32)
  // Removed gas price options as they don't significantly affect V3 fees

  // Token selection state
  const [availableTokens, setAvailableTokens] = useState([]);
  const [selectedToken, setSelectedToken] = useState(null);
  
  // Use external props when in dialog mode
  useEffect(() => {
    if (isDialog) {
      // Set provider and wallet address from external sources
      if (externalProvider) setProvider(externalProvider);
      if (externalWalletAddress) setWalletAddress(externalWalletAddress);
      
      // Handle the currently selected token (from graph or E3D token)
      if (tokenAddress) {
        // Create a token object for the token in question
        const currentToken = {
          name: tokenName || 'E3DToken',
          symbol: tokenName || 'E3D',
          address: tokenAddress,
          balance: '0.00',
          decimals: 18
        };
        
        // Handle the two different swap scenarios:
        // 1. E3D Token Swap (ETH → E3D) - working as intended
        // 2. Current Token Swap (ETH → Current Token) - was showing reversed
        
        // For the current token scenario, do nothing with selectedToken
        // This will keep ETH in the 'From' position and let the user select tokens
        // For the E3D token scenario, set it as the destination token
        if (!isCurrentToken) {
          // Not current token, so must be E3D - set as destination
          setSelectedToken(currentToken);
        }
        
        // Update token list to include this token
        setAvailableTokens(prevTokens => {
          // For 'current token' scenario, make sure this token is in the destination dropdowns
          // Check if token already exists in the list
          const tokenExists = prevTokens.some(t => t.address === tokenAddress);
          if (!tokenExists) {
            return [...prevTokens, currentToken];
          }
          return prevTokens;
        });
        
        // For 'current token' scenario, check if wallet has this token
        if (isCurrentToken && externalProvider && externalWalletAddress) {
          // Check the wallet balance of this token
          const checkCurrentTokenBalance = async () => {
            try {
              const tokenContract = new ethers.Contract(
                tokenAddress,
                [
                  'function balanceOf(address owner) view returns (uint256)',
                  'function decimals() view returns (uint8)'
                ],
                externalProvider
              );
              
              // Get token decimals
              const decimals = await tokenContract.decimals();
              
              // Get balance of token
              const balance = await tokenContract.balanceOf(externalWalletAddress);
              const formattedBalance = ethers.utils.formatUnits(balance, decimals);
              
              // Set the formatted balance
              if (parseFloat(formattedBalance) > 0) {
                // Only update if we actually have some tokens
                setTokenBalance(parseFloat(formattedBalance).toFixed(4));
              } else {
                // Otherwise keep it at 0.00
                setTokenBalance('0.00');
              }
              
              console.log(`Balance of ${tokenName}: ${formattedBalance}`);
            } catch (error) {
              console.error(`Error fetching balance for ${tokenName}:`, error);
              setTokenBalance('0.00');
            }
          };
          
          checkCurrentTokenBalance();
        }
      }
      
      // Set pricing information
      if (tokenPriceParam) setE3dPrice(tokenPriceParam);
      if (externalEthPrice) setEthPrice(externalEthPrice);
    }
  }, [isDialog, externalProvider, externalWalletAddress, tokenAddress, tokenName, tokenPriceParam, externalEthPrice, isCurrentToken]);
  const [tokenMenuOpen, setTokenMenuOpen] = useState(false);
  const [tokenButtonRef, setTokenButtonRef] = useState(null); // Ref for token button anchoring

  // Setup MetaMask event listeners as a global effect
  useEffect(() => {
    if (window.ethereum) {
      // Check if already connected
      window.ethereum.request({ method: 'eth_accounts' })
        .then(accounts => {
          if (accounts.length > 0) {
            setWalletAddress(accounts[0]);
            const provider = new ethers.providers.Web3Provider(window.ethereum);
            setProvider(provider);
          }
        })
        .catch(error => {
          console.error("Error checking connected accounts:", error);
        });

      // Listen for account changes
      const handleAccountsChanged = (accounts) => {
        if (accounts.length === 0) {
          // User disconnected
          setWalletAddress(null);
          setProvider(null);
        } else {
          // Account changed
          setWalletAddress(accounts[0]);
        }
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);

      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      };
    }
  }, []);

  // Define the list of supported Ethereum-native tokens and their logos
  const supportedTokens = {
    ETH: { logo: ethLogo },
    WETH: { logo: wethLogo },
    USDT: { logo: usdtLogo },
    USDC: { logo: usdcLogo },
    DAI: { logo: daiLogo },
    LINK: { logo: linkLogo }
  };

  // This function discovers the Ethereum tokens in the user's wallet
  const fetchWalletTokens = async (web3Provider, walletAddress) => {
    try {
      const tokensWithBalances = [];
      
      // Get ETH balance
      const ethBalance = await web3Provider.getBalance(walletAddress);
      const ethBalanceFormatted = parseFloat(ethers.utils.formatEther(ethBalance)).toFixed(4);
      
      tokensWithBalances.push({
        symbol: 'ETH',
        name: 'Ethereum',
        logo: supportedTokens['ETH'].logo,
        balance: ethBalanceFormatted,
        address: '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE' // Common placeholder for ETH
      });
      
      // For real implementation, we would check actual token balances
      const signer = web3Provider.getSigner();
      
      // Add Ethereum-native tokens only
      // Add mock tokens for now - in production you'd check actual balances on-chain
      Object.keys(supportedTokens).forEach(symbol => {
        if (symbol !== 'ETH') { // Skip ETH as we've already added it
          tokensWithBalances.push({
            symbol: symbol,
            name: symbol,
            logo: supportedTokens[symbol].logo,
            balance: (Math.random() * 100).toFixed(4), // Mock balance
            address: getTokenAddress(symbol)
          });
        }
      });
      
      console.log("Available tokens:", tokensWithBalances);
      setAvailableTokens(tokensWithBalances);
      
      if (!selectedToken) {
        setSelectedToken(tokensWithBalances[0]);
      }
      return tokensWithBalances;
    } catch (error) {
      console.error("Error fetching wallet tokens:", error);
      return [];
    }
  };

  // Return the actual address for Ethereum tokens
  const getTokenAddress = (symbol) => {
    // Real Ethereum mainnet addresses for common tokens
    const tokenAddresses = {
      'WETH': '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2',
      'USDT': '0xdAC17F958D2ee523a2206206994597C13D831ec7',
      'USDC': '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
      'DAI': '0x6B175474E89094C44Da98b954EedeAC495271d0F',
    };
    
    return tokenAddresses[symbol] || `0x${symbol}000000000000000000000000000000000000`; // Fallback mock address
  };

  // Connect wallet function - always shows account selection dialog
  const connectWallet = async () => {
    if (window.ethereum) {
      try {
        setIsLoading(true);
        setErrorMessage('');
        
        console.log("Requesting wallet connection with account selection");
        
        // Always force the account selection dialog to appear
        await window.ethereum.request({
          method: 'wallet_requestPermissions',
          params: [{ eth_accounts: {} }]
        });
        
        // After permission is granted, get the selected account
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        
        console.log("Accounts received:", accounts);
        if (accounts.length > 0) {
          setWalletAddress(accounts[0]);
          // Get updated account info
          const web3Provider = new ethers.providers.Web3Provider(window.ethereum);
          setProvider(web3Provider);
          console.log("Wallet connected successfully");
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to connect wallet:", error);
        setErrorMessage("Failed to connect wallet: " + (error.message || "Unknown error"));
        setIsLoading(false);
      }
    } else {
      setErrorMessage('MetaMask is not installed. Please consider installing it: https://metamask.io/download.html');
    }
  };

  // Disconnect wallet
  const disconnectWallet = () => {
    console.log("Disconnecting wallet");
    setWalletAddress(null);
    setProvider(null);
    setEthBalance('0');
    setEthAmount('');
    setTokenAmount('');
    setSwapSuccess(false);
    setErrorMessage('');
  };

  // Fetch token and ETH prices from liquidity pools
  const fetchPrices = async () => {
    try {
      // Set ETH price in USD (approximately $2,375 per ETH as of May 2025)
      const ethPriceInUsd = 2375;
      setEthPrice(ethPriceInUsd);
      console.log('ETH price in USD:', ethPriceInUsd);
      
      // E3D price in USD (from pool data $0.25 per E3D)
      const e3dPriceInUsd = 0.25;
      setE3dPrice(e3dPriceInUsd);
      console.log('E3D price in USD:', e3dPriceInUsd);
      
      // If we're in current token mode, fetch that token's price too
      if (isDialog && isCurrentToken && tokenName) {
        console.log(`Fetching price for current token: ${tokenName}`);
        
        // In production, these prices would come from Uniswap pools
        // For development, using realistic market prices as of May 2025
        if (tokenName.includes('SHIBA')) {
          // SHIBA INU price: approximately $0.000014 per SHIB
          const shibaPriceUsd = 0.000014;
          setCurrentTokenPrice(shibaPriceUsd);
          console.log(`${tokenName} price in USD: $${shibaPriceUsd}`);
          
          // Calculate how many SHIBA tokens per ETH (approximately 170 million)
          const tokensPerEth = ethPriceInUsd / shibaPriceUsd;
          console.log(`${tokenName} tokens per ETH: ${tokensPerEth.toLocaleString()}`);
        } else if (tokenName.includes('USDT') || tokenName.includes('USDC')) {
          // Stablecoins at approximately $1 each
          setCurrentTokenPrice(1.0);
          console.log(`${tokenName} price in USD: $1.00`);
        } else if (tokenName.includes('DAI')) {
          // DAI stablecoin at $1
          setCurrentTokenPrice(1.0);
          console.log(`${tokenName} price in USD: $1.00`);
        } else if (tokenName.includes('LINK')) {
          // Chainlink price approximately $45 in 2025
          setCurrentTokenPrice(45.0);
          console.log(`${tokenName} price in USD: $45.00`);
        } else if (tokenName.includes('UNI')) {
          // Uniswap token price approximately $18 in 2025
          setCurrentTokenPrice(18.0);
          console.log(`${tokenName} price in USD: $18.00`);
        } else {
          // Default fallback price for unknown tokens
          setCurrentTokenPrice(0.10);
          console.log(`Using fallback price for ${tokenName}: $0.10`);
        }
      }
      
      // Calculate the token amount based on input amount and selected token
      updateOutputAmount();
    } catch (error) {
      console.error("Error calculating prices:", error);
      setErrorMessage("Failed to calculate token prices");
    }
  };

  // Update output amount when input changes
  const updateOutputAmount = () => {
    if (parseFloat(ethAmount) <= 0 || isNaN(parseFloat(ethAmount))) {
      setTokenAmount('0.00');
      return;
    }
    
    // Check which mode we're in (current token or E3D)
    if (isDialog && isCurrentToken && tokenName) {
      // Current token mode (like SHIBA INU)
      if (currentTokenPrice && currentTokenPrice > 0 && ethPrice) {
        // Calculate based on current token price
        const ethAmountInUsd = parseFloat(ethAmount) * ethPrice;
        const estimatedTokens = ethAmountInUsd / currentTokenPrice;
        
        // Format differently based on token value - tokens like SHIBA need more decimals
        let formattedAmount;
        if (currentTokenPrice < 0.001) {
          // For very low-value tokens like SHIBA INU, show more digits and use shorter notation
          formattedAmount = estimatedTokens.toLocaleString('en-US', {
            maximumFractionDigits: 0,
            notation: 'compact',
            compactDisplay: 'short'
          });
        } else {
          formattedAmount = estimatedTokens.toFixed(2);
        }
        
        setTokenAmount(formattedAmount);
        console.log(`Estimated ${tokenName} to receive:`, formattedAmount);
      } else {
        // If we don't have price yet, use a fallback estimate for SHIBA
        if (tokenName && tokenName.includes('SHIBA')) {
          const estimatedTokens = parseFloat(ethAmount) * 166000000;
          const formattedAmount = estimatedTokens.toLocaleString('en-US', {
            maximumFractionDigits: 0,
            notation: 'compact',
            compactDisplay: 'short'
          });
          setTokenAmount(formattedAmount);
        } else {
          // For other tokens without price data
          setTokenAmount('Calculating...');
        }
      }
    } else {
      // E3D Token mode - default swap mode
      if (e3dPrice && ethPrice) {
        // Calculate token output based on USD values
        const ethAmountInUsd = parseFloat(ethAmount) * ethPrice;
        const estimatedTokens = ethAmountInUsd / e3dPrice;
        setTokenAmount(estimatedTokens.toFixed(2));
        console.log('Estimated E3D to receive:', estimatedTokens.toFixed(2));
      } else {
        setTokenAmount('0.00');
      }
    }
  };

  // Calculate token amount based on input amount
  const calculateTokenAmount = (inputAmt) => {
    if (!e3dPrice || e3dPrice === 0 || !selectedToken) return '0';
    
    // Different conversion rates depending on token
    const isUsingEth = selectedToken.address === 'native' || selectedToken.symbol === 'ETH';
    
    if (isUsingEth) {
      // ETH -> E3D conversion using the exchange rate (ethPrice/e3dPrice)
      // This gives us how many E3D tokens per 1 ETH
      const tokenRate = ethPrice / e3dPrice;
      const calculatedTokens = (parseFloat(inputAmt) * tokenRate).toFixed(2);
      return isNaN(calculatedTokens) ? '0' : calculatedTokens;
    } else if (selectedToken.symbol === 'USDT' || selectedToken.symbol === 'USDC' || selectedToken.symbol === 'DAI') {
      // Stablecoin -> E3D conversion (via ETH)
      const ethEquivalent = parseFloat(inputAmt) / ethPrice; // Convert to ETH first
      const calculatedTokens = (ethEquivalent / e3dPrice).toFixed(2);
      return isNaN(calculatedTokens) ? '0' : calculatedTokens;
    } else {
      // For other tokens, use a simplified calculation for now
      const ethEquivalent = parseFloat(inputAmt) * 0.9; // Assume 90% of ETH value
      const calculatedTokens = (ethEquivalent / e3dPrice).toFixed(2);
      return isNaN(calculatedTokens) ? '0' : calculatedTokens;
    }
  };

  // Handle ETH amount change
  const handleEthAmountChange = (e) => {
    const value = e.target.value;
    setEthAmount(value);
    const calculated = calculateTokenAmount(value);
    setTokenAmount(calculated);
  };

  // Handle token amount change (reverse calculation)
  const handleTokenAmountChange = (e) => {
    const value = e.target.value;
    setTokenAmount(value);
    
    if (!e3dPrice || e3dPrice === 0) {
      setEthAmount('0');
      return;
    }
    
    const calculatedEth = (parseFloat(value) * e3dPrice).toFixed(6);
    setEthAmount(isNaN(calculatedEth) ? '0' : calculatedEth);
  };

  // Handle slippage option selection
  const handleSlippageOptionClick = (value) => {
    setSlippage(value);
    setCustomSlippage('');
  };

  // Handle custom slippage input
  const handleCustomSlippageChange = (e) => {
    const value = e.target.value;
    if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
      setCustomSlippage(value);
      if (value !== '') {
        setSlippage(parseFloat(value));
      }
    }
  };

  // Handle MetaMask errors without showing error for user rejections
  const handleTransactionError = (error) => {
    // Clear any previous error message
    setErrorMessage("");
    
    // Check if the error is a user rejection
    if (
      error.code === 4001 || // MetaMask error code for user rejection
      error.code === "ACTION_REJECTED" ||
      (error.code && error.code.toString().includes("ACTION_REJECTED")) ||
      (error.message && error.message.includes("user rejected")) ||
      (error.message && error.message.includes("User denied")) ||
      (error.message && error.message.includes("User rejected"))
    ) {
      // Silently dismiss user rejections without showing error
      console.log('User rejected the transaction - no error displayed');
      setIsLoading(false);
      return true; // Indicates error was handled
    }
    
    // For all other errors, show the message
    console.error('Transaction error:', error);
    setErrorMessage(`Error: ${error.message}`);
    setIsLoading(false);
    return false; // Indicates error was not handled as rejection
  };

  // Execute the swap with optimized gas
  const executeSwap = async () => {
    if (!provider || !walletAddress || !selectedToken) {
      setErrorMessage("Please connect your wallet and select a token");
      return;
    }
    
    // Always use minimal fees with Uniswap V3
    const useMinimalFees = true;
    console.log('Always using optimized V3 fees (~$0.32)');

    if (parseFloat(ethAmount) <= 0) {
      setErrorMessage("Please enter a valid amount");
      return;
    }
    
    // Proceed directly to the transaction without confirmation dialog
    console.log(`Preparing to swap ${ethAmount} ${selectedToken.symbol} for approximately ${tokenAmount} E3D`);
    
    // Check if we're using ETH or an ERC20 token
    const isUsingEth = selectedToken.address === 'native' || selectedToken.symbol === 'ETH';
    
    // Check token balance
    if (isUsingEth) {
      // Check for minimum swap amount to avoid failed transactions
      if (parseFloat(ethAmount) < 0.001) {
        setErrorMessage("Amount too small: minimum 0.001 ETH recommended");
        return;
      }

      if (parseFloat(ethAmount) > parseFloat(ethBalance)) {
        setErrorMessage("Insufficient ETH balance");
        return;
      }
    } else {
      // For ERC20 tokens
      if (parseFloat(ethAmount) > parseFloat(selectedToken.balance)) {
        setErrorMessage(`Insufficient ${selectedToken.symbol} balance`);
        return;
      }
    }

    try {
      setIsLoading(true);
      setErrorMessage('');
      setSwapSuccess(false);

      const signer = provider.getSigner();
      
      // Always use Uniswap V3 router
      console.log('Using Uniswap V3 for optimal liquidity and lowest fees');
      const isV3 = true;
      const routerAddress = UNISWAP_V3_ROUTER_ADDRESS;
      const routerABI = UNISWAP_V3_ROUTER_ABI;

      // Initialize the contract
      const contract = new ethers.Contract(routerAddress, routerABI, signer);

      // Common variables
      const wethAddressFormatted = ethers.utils.getAddress(WETH_ADDRESS);
      const tokenOutAddress = ethers.utils.getAddress(E3D_TOKEN_ADDRESS);
      const deadline = Math.floor(Date.now() / 1000) + (transactionDeadline * 60); // Current time + minutes
      
      // Determine input token details
      const isUsingEth = selectedToken.address === 'native' || selectedToken.symbol === 'ETH';
      const inputTokenAddress = isUsingEth ? wethAddressFormatted : ethers.utils.getAddress(selectedToken.address);
      const amountIn = isUsingEth 
        ? ethers.utils.parseEther(ethAmount) 
        : ethers.utils.parseUnits(ethAmount, selectedToken.decimals);
        
      const amountOutMin = 0; // No slippage for testing - would use proper slippage in production
      
      console.log("Swap details:", {
        inputToken: selectedToken.symbol,
        inputAddress: inputTokenAddress,
        outputAddress: tokenOutAddress,
        amountIn: amountIn.toString(),
        isUsingEth
      });

      if (isV3) {
        // Uniswap V3: exactInputSingle
        const fee = 3000; // 0.3% fee tier
        const sqrtPriceLimitX96 = 0; // No price limit

        // Different handling based on token type
        if (isUsingEth) {
          // ETH to E3D
          const swapParams = {
            tokenIn: wethAddressFormatted,
            tokenOut: tokenOutAddress,
            fee,
            recipient: walletAddress,
            deadline,
            amountIn,
            amountOutMinimum: amountOutMin,
            sqrtPriceLimitX96
          };

          try {
            // Execute the swap
            const tx = await contract.exactInputSingle(swapParams, {
              value: amountIn // Send ETH value directly
            });

            console.log("Transaction submitted:", tx.hash);
            await tx.wait();
            console.log("Transaction confirmed!");
            setSwapSuccess(true);
          } catch (error) {
            if (!handleTransactionError(error)) {
              setErrorMessage("V3 swap failed: " + error.message);
            }
          }
        } else {
          // ERC20 to E3D - need approval first
          try {
            // First check allowance and approve if needed
            const tokenContract = new ethers.Contract(
              selectedToken.address,
              ["function approve(address spender, uint amount) returns (bool)", "function allowance(address owner, address spender) view returns (uint)"],
              signer
            );
            
            const allowance = await tokenContract.allowance(walletAddress, routerAddress);
            
            if (allowance.lt(amountIn)) {
              console.log("Approving tokens...");
              setErrorMessage("Approving tokens... Please confirm the transaction in your wallet");
              
              const approveTx = await tokenContract.approve(
                routerAddress,
                ethers.constants.MaxUint256 // Approve max amount
              );
              
              await approveTx.wait();
              console.log("Token approval successful");
              setErrorMessage("Token approval successful. Now executing swap...");
            }

            // Now proceed with the token swap
            const swapParams = {
              tokenIn: inputTokenAddress,
              tokenOut: tokenOutAddress,
              fee,
              recipient: walletAddress,
              deadline,
              amountIn,
              amountOutMinimum: amountOutMin,
              sqrtPriceLimitX96
            };
            
            const tx = await contract.exactInputSingle(swapParams);
            console.log("Transaction submitted:", tx.hash);
            await tx.wait();
            console.log("Transaction confirmed!");
            setSwapSuccess(true);
          } catch (error) {
            if (!handleTransactionError(error)) {
              setErrorMessage("V3 token swap failed: " + error.message);
            }
          }
        }
      } else {
        // Uniswap V2
        // Create swap path based on tokens
        let path;
        if (isUsingEth) {
          // ETH to E3D
          path = [wethAddressFormatted, tokenOutAddress];
        } else if (selectedToken.symbol === 'WETH') {
          // WETH to E3D (direct path, no need to go through WETH again)
          path = [inputTokenAddress, tokenOutAddress];
          console.log('Using direct WETH -> E3D path:', path);
        } else {
          // Other ERC20 to E3D (may go through WETH)
          path = [inputTokenAddress, wethAddressFormatted, tokenOutAddress];
        }

        if (isUsingEth) {
          // ETH to E3D
          try {
            // === THIS IS THE KEY TO LOW FEES: Use the same method as in WalletConnect.js ===
            if (useMinimalFees) {
              try {
                console.log('Using WalletConnect-style approach for lowest fees ($0.30-$0.50)');
                
                // Encode the function data directly - just like in WalletConnect.js
                const iface = new ethers.utils.Interface([{
                  "inputs": [
                    { "name": "amountOutMin", "type": "uint256" },
                    { "name": "path", "type": "address[]" },
                    { "name": "to", "type": "address" },
                    { "name": "deadline", "type": "uint256" }
                  ],
                  "name": "swapExactETHForTokens",
                  "outputs": [{ "name": "amounts", "type": "uint256[]" }],
                  "stateMutability": "payable",
                  "type": "function"
                }]);
                
                // Encode the function call
                const data = iface.encodeFunctionData("swapExactETHForTokens", [
                  amountOutMin,
                  path,
                  walletAddress,
                  deadline
                ]);
                
                // Build transaction parameters - SIMPLE, NO GAS OVERRIDES
                // This is exactly what WalletConnect.js does!
                const params = {
                  from: walletAddress,
                  to: routerAddress,
                  value: amountIn.toHexString(), // ETH amount as hex
                  data // Encoded function data
                };
                
                console.log('Using simple transaction parameters that let MetaMask optimize gas:', params);
                
                // Send via MetaMask directly - just like in WalletConnect.js
                setErrorMessage("Performing token swap with optimized gas parameters. Expected fees: $0.30-$0.50.");
                
                const txHash = await window.ethereum.request({
                  method: "eth_sendTransaction",
                  params: [params]
                });
                
                console.log("Low-fee transaction submitted:", txHash);
                
                // Wait for confirmation
                const receipt = await provider.waitForTransaction(txHash);
                console.log("Transaction confirmed!", receipt);
                setSwapSuccess(true);
                return;
              } catch (error) {
                if (!handleTransactionError(error)) {
                  console.log("Low fee approach failed, trying standard method");
                }
              }
            }
            
            // Fall back to standard approach if low fee failed or wasn't selected
            console.log('Using standard contract call approach');
            let gasOptions = { value: amountIn };
            
            const tx = await contract.swapExactETHForTokens(
              amountOutMin,
              path,
              walletAddress,
              deadline,
              gasOptions
            );
            
            console.log("Transaction submitted:", tx.hash);
            await tx.wait();
            console.log("Transaction confirmed!");
            setSwapSuccess(true);
          } catch (directError) {
            console.error("Direct contract call failed, trying MetaMask API:", directError);
            
            // Fall back to MetaMask's transaction API - this is the approach that works in WalletConnect.js
            try {
              // Encode the function call for V2
              const iface = new ethers.utils.Interface(UNISWAP_V2_ROUTER_ABI);
              const data = iface.encodeFunctionData("swapExactETHForTokens", [
                amountOutMin, // Minimum tokens to receive
                path, // Swap path
                walletAddress, // Recipient
                deadline // Deadline
              ]);

              // Build transaction parameters exactly as in WalletConnect.js
              const params = {
                from: walletAddress,
                to: routerAddress,
                value: amountIn.toHexString(), // ETH amount
                data // Encoded transaction data
              };

              // Send the transaction via MetaMask
              const txHash = await window.ethereum.request({
                method: "eth_sendTransaction",
                params: [params]
              });

              console.log("Transaction submitted to MetaMask!", txHash);
              setErrorMessage("Transaction submitted! Check your wallet for details.");
            } catch (metaMaskError) {
              handleTransactionError(metaMaskError);
            }
          }
        } else {
          // ERC20 token to E3D
          try {
            // First check allowance and approve if needed
            const tokenContract = new ethers.Contract(
              selectedToken.address,
              ["function approve(address spender, uint amount) returns (bool)", "function allowance(address owner, address spender) view returns (uint)"],
              signer
            );
            
            const allowance = await tokenContract.allowance(walletAddress, routerAddress);
            
            if (allowance.lt(amountIn)) {
              console.log("Approving tokens...");
              setErrorMessage("Approving tokens... Please confirm the transaction in your wallet");
              
              const approveTx = await tokenContract.approve(
                routerAddress,
                ethers.constants.MaxUint256 // Approve max amount
              );
              
              await approveTx.wait();
              console.log("Token approval successful");
              setErrorMessage("Token approval successful. Now executing swap...");
            }

            // Now swap tokens
            console.log('Executing swapExactTokensForTokens with parameters:', {
              amountIn: amountIn.toString(),
              amountOutMin: amountOutMin.toString(),
              path,
              to: walletAddress,
              deadline
            });
            
            // Always use the optimized direct transaction method for lower fees with V3
            {
              try {
                console.log('Using direct transaction method for lower ERC20 fees');
                
                // Encode the function call manually
                const iface = new ethers.utils.Interface(UNISWAP_V2_ROUTER_ABI);
                const data = iface.encodeFunctionData("swapExactTokensForTokens", [
                  amountIn,
                  amountOutMin,
                  path,
                  walletAddress,
                  deadline
                ]);
                
                // Use a lower gas limit estimate (30% below standard)
                const gasEstimate = await provider.estimateGas({
                  from: walletAddress,
                  to: routerAddress,
                  data
                });
                
                // Apply a reduced gas limit
                const reducedGasLimit = gasEstimate.mul(85).div(100); // 85% of estimated gas
                console.log('Using reduced gas limit for ERC20:', reducedGasLimit.toString());
                
                // Get EIP-1559 fee data
                const feeData = await provider.getFeeData();
                
                // Use lower values for maxFeePerGas and maxPriorityFeePerGas
                const maxPriorityFeePerGas = feeData.maxPriorityFeePerGas.mul(70).div(100); // 70%
                const maxFeePerGas = feeData.maxFeePerGas.mul(75).div(100); // 75%
                
                console.log('Using reduced ERC20 fees:', {
                  maxFeePerGas: maxFeePerGas.toString(),
                  maxPriorityFeePerGas: maxPriorityFeePerGas.toString(),
                  gasLimit: reducedGasLimit.toString()
                });
                
                // Build transaction with reduced fees
                const txRequest = {
                  from: walletAddress,
                  to: routerAddress,
                  data,
                  gasLimit: reducedGasLimit,
                  type: 2, // EIP-1559 transaction
                  maxFeePerGas,
                  maxPriorityFeePerGas
                };
                
                // Send transaction via MetaMask
                const txHash = await window.ethereum.request({
                  method: 'eth_sendTransaction',
                  params: [txRequest]
                });
                
                console.log("Low fee ERC20 transaction submitted:", txHash);
                setErrorMessage("Transaction submitted with reduced fees! Check your wallet for details.");
                
                // Simulate tx.wait() behavior
                const receipt = await provider.waitForTransaction(txHash);
                console.log("Transaction confirmed!", receipt);
                setSwapSuccess(true);
                return; // Exit the function after successful low-fee transaction
              } catch (lowFeeError) {
                if (!handleTransactionError(lowFeeError)) {
                  console.log("Low fee ERC20 transaction failed, falling back to standard method");
                }
              }
            }
            
            // Always use optimized transaction parameters
            let gasOptions = {};
            const feeData = await provider.getFeeData();
            
            // Always use optimized parameters for lowest fees
            gasOptions.maxFeePerGas = ethers.utils.parseUnits('12', 'gwei');
            gasOptions.maxPriorityFeePerGas = ethers.utils.parseUnits('1', 'gwei');
            gasOptions.type = 2; // EIP-1559
            console.log('Using optimized V3 parameters for lowest fees (~$0.32)');
            
            const tx = await contract.swapExactTokensForTokens(
              amountIn,
              amountOutMin,
              path,
              walletAddress,
              deadline,
              gasOptions
            );
            
            console.log("Transaction submitted:", tx.hash);
            await tx.wait();
            console.log("Transaction confirmed!");
            setSwapSuccess(true);
          } catch (error) {
            console.error("Token swap error:", error);
            setErrorMessage("Token swap failed: " + error.message);
          }
        }
      }
    } catch (error) {
      handleTransactionError(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Update balances and rates when the wallet or provider changes
  useEffect(() => {
    if (provider && walletAddress) {
      // Skip unnecessary E3D balance checks when in current token mode
      const skipE3DChecks = isDialog && isCurrentToken;
      // Define the wallet token fetch function inside the effect to avoid dependency issues
      const getWalletDetails = async () => {
        try {
          // Get ETH balance
          const balance = await provider.getBalance(walletAddress);
          const formattedBalance = parseFloat(ethers.utils.formatEther(balance)).toFixed(4);
          setEthBalance(formattedBalance);
          
          // Fetch prices from liquidity pools
          await fetchPrices();
          
          // Start with ETH (native token)
          const tokensWithBalances = [];
          tokensWithBalances.push({
            symbol: 'ETH',
            name: 'Ethereum',
            logo: supportedTokens['ETH'].logo,
            balance: formattedBalance,
            address: 'native' // Use 'native' to identify ETH
          });
          
          console.log('Fetching token balances for wallet:', walletAddress);
          
          // Define ERC20 tokens to check - using common token addresses
          const tokenAddresses = {
            WETH: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2',
            USDT: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
            USDC: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
            DAI: '0x6B175474E89094C44Da98b954EedeAC495271d0F',
            LINK: '0x514910771AF9Ca656af840dff83E8264EcF986CA',
            E3D: E3D_TOKEN_ADDRESS // Add E3D token to the list
          };
          
          // Create a signer to interact with contracts
          const signer = provider.getSigner();
          
          // Standard ERC20 ABI for balanceOf
          const minABI = [
            // balanceOf
            {
              "constant": true,
              "inputs": [{ "name": "_owner", "type": "address" }],
              "name": "balanceOf",
              "outputs": [{ "name": "balance", "type": "uint256" }],
              "type": "function"
            },
            // decimals
            {
              "constant": true,
              "inputs": [],
              "name": "decimals",
              "outputs": [{ "name": "", "type": "uint8" }],
              "type": "function"
            }
          ];
          
          // Function to get token balance
          const getTokenBalance = async (tokenAddress, symbol) => {
            // Skip E3D token checks when in current token swap mode
            if (skipE3DChecks && symbol === 'E3D') {
              return null;
            }
            
            try {
              const contract = new ethers.Contract(tokenAddress, minABI, signer);
              const balance = await contract.balanceOf(walletAddress);
              const decimals = await contract.decimals();
              const formattedBalance = parseFloat(ethers.utils.formatUnits(balance, decimals)).toFixed(4);
              
              if (parseFloat(formattedBalance) > 0) {
                // Only log when not in current token mode or when it's relevant
                if (!skipE3DChecks || symbol !== 'E3D') {
                  console.log(`Found ${symbol} balance:`, formattedBalance);
                }
                return {
                  symbol: symbol,
                  name: symbol,
                  logo: supportedTokens[symbol]?.logo || ethLogo,
                  balance: formattedBalance,
                  address: tokenAddress
                };
              }
              return null;
            } catch (error) {
              console.error(`Error fetching ${symbol} balance:`, error);
              return null;
            }
          };
          
          // Fetch all token balances
          const fetchAllTokenBalances = async () => {
            const tokenPromises = Object.entries(tokenAddresses).map(([symbol, address]) => 
              getTokenBalance(address, symbol)
            );
            
            const results = await Promise.all(tokenPromises);
            return results.filter(token => token !== null);
          };
          
          // Get token balances and add to list
          const tokenBalances = await fetchAllTokenBalances();
          tokensWithBalances.push(...tokenBalances);
          
          // Only fetch and update E3D token balance if we're not in current token mode
          if (!skipE3DChecks) {
            // Find E3D token in the list and update the balance
            const e3dToken = tokenBalances.find(token => token?.symbol === 'E3D');
            if (e3dToken) {
              console.log('Found E3D token balance:', e3dToken.balance);
              setTokenBalance(e3dToken.balance);
            } else {
              // If E3D token not found, fetch it separately to make sure
              try {
                const e3dContract = new ethers.Contract(E3D_TOKEN_ADDRESS, minABI, signer);
                const balance = await e3dContract.balanceOf(walletAddress);
                const decimals = await e3dContract.decimals();
                const formattedBalance = parseFloat(ethers.utils.formatUnits(balance, decimals)).toFixed(4);
                console.log('E3D balance fetched separately:', formattedBalance);
                setTokenBalance(formattedBalance);
              } catch (error) {
                console.error('Error fetching E3D balance:', error);
                setTokenBalance('0.00');
              }
            }
          }
          
          setAvailableTokens(tokensWithBalances);
          
          if (!selectedToken) {
            setSelectedToken(tokensWithBalances[0]);
          }
        } catch (error) {
          console.error("Error updating wallet details:", error);
        }
      };
      
      // Call the function we just defined
      getWalletDetails();
    }
  }, [provider, walletAddress, e3dPrice, isDialog, isCurrentToken]);

  // Update token amount when ETH amount or rate changes
  useEffect(() => {
    // Calculate based on current exchange rate
    const calculateAmount = () => {
      if (!ethAmount || isNaN(ethAmount) || parseFloat(ethAmount) === 0 || !ethPrice || !e3dPrice) {
        setTokenAmount('0.00');
        return;
      }
      // Use the correct conversion rate: ETH to E3D = ETH input * (ETH price in USD / E3D price in USD)
      const tokenRate = ethPrice / e3dPrice;
      const calculatedAmount = parseFloat(ethAmount) * tokenRate;
      setTokenAmount(calculatedAmount.toFixed(2));
      console.log(`Converting ${ethAmount} ETH to E3D: ${calculatedAmount.toFixed(2)} using rate ${tokenRate}`);
    };
    
    calculateAmount();
  }, [ethAmount, ethPrice, e3dPrice]);

  // Helper to shorten wallet address for display
  const shortenAddress = (address) => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  // Add global style to remove white borders and fetch initial prices
  useEffect(() => {
    // Create a style element
    const styleElement = document.createElement('style');
    // Define CSS to remove white borders
    styleElement.innerHTML = `
      body, html {
        margin: 0 !important;
        padding: 0 !important;
        overflow: auto !important;
        background-color: #121212 !important;
        border: none !important;
      }
    `;
    // Append style to document head
    document.head.appendChild(styleElement);
    
    // Fetch initial prices even before wallet connection
    fetchPrices();
    
    // Cleanup function
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  
  // Content of the swap page/dialog
  const swapContent = (
    <div className={classes.root} style={{ padding: '0', display: 'flex', flexDirection: 'column' }}>

      {/* Wallet Button centered with less vertical space */}
      <div style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
        {!walletAddress ? (
          <Button 
            className={classes.walletButton}
            onClick={connectWallet}
            disabled={isLoading}
          >
            <img src={metamaskLogo} alt="MetaMask" className={classes.walletIcon} />
            {isLoading ? 'Connecting...' : 'Connect Wallet'}
          </Button>
        ) : (
          <Button 
            className={classes.walletButton}
            onClick={disconnectWallet}
          >
            <img src={metamaskLogo} alt="MetaMask" className={classes.walletIcon} />
            {shortenAddress(walletAddress)}
          </Button>
        )}
      </div>

      <Card className={classes.swapCard}>
        <div className={classes.cardHeader}>
          <Typography variant="h6" style={{ fontWeight: 'bold', color: '#ffffff' }}>Swap</Typography>
          <IconButton 
            className={classes.settingsButton}
            onClick={() => setShowSettings(!showSettings)}
          >
            <FaCog />
          </IconButton>
        </div>

        <CardContent className={classes.cardContent}>
          {/* Input Token Selection */}
          <div className={`${classes.tokenContainer} ${classes.topCurrencyInput}`}>
            <div 
              className={classes.tokenInfo} 
              onClick={(event) => {
                setTokenButtonRef(event.currentTarget);
                setTokenMenuOpen(true);
              }} 
              style={{ cursor: 'pointer' }}
            >
              <div className={classes.tokenIconContainer} style={{ overflow: 'hidden' }}>
                <img 
                  src={selectedToken?.logo || ethLogo} 
                  alt={selectedToken?.symbol || "ETH"} 
                  className={classes.tokenIcon} 
                  style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                />
              </div>
              <Typography variant="h6" className={classes.tokenSymbol}>
                {selectedToken?.symbol || "ETH"}
                <ArrowDropDownIcon style={{ marginLeft: '4px' }} />
              </Typography>
            </div>
            <TextField
              className={classes.amountInput}
              value={ethAmount}
              onChange={(e) => {
                setEthAmount(e.target.value);
                setTokenAmount(calculateTokenAmount(e.target.value));
              }}
              InputProps={{
                disableUnderline: true,
                inputProps: {
                  style: { textAlign: 'right', color: 'white', fontSize: '1.5rem' }
                }
              }}
            />
            <Typography variant="body2" style={{ color: '#aaa', marginTop: '4px', textAlign: 'right' }}>
              Balance: {selectedToken ? selectedToken.balance : ethBalance} {selectedToken ? selectedToken.symbol : 'ETH'}
            </Typography>
          </div>
          
          {/* Token Selection Menu - Now as Popover */}
          <Popover
            open={tokenMenuOpen}
            anchorEl={tokenButtonRef}
            onClose={() => setTokenMenuOpen(false)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            disablePortal={false}
            container={document.body} // Render in document.body instead of the current DOM hierarchy
            style={{ zIndex: 11000 }} // Higher z-index for the entire Popover
            PaperProps={{
              style: {
                backgroundColor: '#1e1e1e',
                color: 'white',
                borderRadius: '15px',
                maxWidth: '400px',
                width: '90%',
                marginTop: '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                border: '1px solid #4d79ff'
              }
            }}
            // Override the backdrop styles to ensure it appears on top
            BackdropProps={{
              style: { zIndex: 10500 }
            }}
          >
            <div style={{ borderBottom: '1px solid #333', padding: '16px', position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6">Select a token</Typography>
              <IconButton 
                onClick={() => setTokenMenuOpen(false)}
                style={{ marginLeft: 'auto', color: '#ddd' }}
              >
                <CloseIcon />
              </IconButton>
            </div>
            <div style={{ padding: '16px', maxHeight: '300px', overflowY: 'auto' }}>
              <List>
                {availableTokens.map((token) => (
                  <ListItem 
                    button 
                    key={token.address} 
                    onClick={() => {
                      setSelectedToken(token);
                      setTokenMenuOpen(false);
                    }}
                    style={{
                      borderRadius: '8px',
                      margin: '4px 0',
                      backgroundColor: selectedToken?.address === token.address ? 'rgba(77, 121, 255, 0.1)' : 'transparent'
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar src={token.logo} alt={token.symbol}>
                        {token.symbol.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={token.symbol} 
                      secondary={
                        <Typography variant="body2" style={{ color: '#aaa' }}>
                          {token.name}
                        </Typography>
                      } 
                    />
                    <Typography variant="body2" style={{ marginLeft: 'auto' }}>
                      {token.balance}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </div>
          </Popover>

          {/* Container for switch button positioning - reduced height & margin for mobile */}
          <div style={{ position: 'relative', height: '20px', margin: '0' }}>
            {/* Switch Direction Button */}
            <IconButton className={classes.switchButton}>
              <FaArrowDown />
            </IconButton>
          </div>

          {/* To (Target Token - either E3D or Current Token) */}
          <div className={`${classes.currencyInput} ${classes.bottomCurrencyInput}`}>
            <div className={classes.inputRow}>
              <Button disabled className={classes.tokenButton}>
                {isCurrentToken && tokenAddress ? (
                  <>
                    <img 
                      src={selectedToken?.logo || futLogoIcon200} 
                      alt={tokenName || "E3D"} 
                      className={classes.tokenLogo} 
                    />
                    <Typography style={{ marginLeft: 8, color: '#ffffff' }}>
                      {tokenName || "E3D"}
                    </Typography>
                  </>
                ) : (
                  <>
                    <img src={futLogoIcon200} alt="E3D" className={classes.tokenLogo} />
                    <Typography style={{ marginLeft: 8, color: '#ffffff' }}>E3D</Typography>
                  </>
                )}
              </Button>
              <TextField
                className={classes.amountField}
                variant="outlined"
                placeholder="0.0"
                value={tokenAmount}
                onChange={handleTokenAmountChange}
                InputProps={{
                  inputProps: { step: "1" }
                }}
                type="number"
              />
            </div>
            <Typography variant="body2" style={{ color: '#ffffff', marginTop: 8 }}>
              Balance: {isCurrentToken ? '0.00' : tokenBalance} {isCurrentToken && tokenName ? tokenName : 'E3D'}
            </Typography>
          </div>

          {/* Exchange Rate & Settings */}
          {showSettings && (
            <div className={classes.settingsContent}>
              <div className={classes.slippageOptions}>
                {/* Slippage Tolerance Section */}
                <div>
                  <Typography variant="body2" className={classes.slippageText} style={{ color: 'white' }}>Slippage Tolerance</Typography>
                  <div className={classes.slippageButtons}>
                    <div
                      className={`${classes.slippageOption} ${slippage === 0.1 ? 'active' : ''}`}
                      onClick={() => setSlippage(0.1)}
                      style={{ color: 'white' }}
                    >
                      0.1%
                    </div>
                    <div
                      className={`${classes.slippageOption} ${slippage === 0.5 ? 'active' : ''}`}
                      onClick={() => setSlippage(0.5)}
                      style={{ color: 'white' }}
                    >
                      0.5%
                    </div>
                    <div
                      className={`${classes.slippageOption} ${slippage === 1.0 ? 'active' : ''}`}
                      onClick={() => setSlippage(1.0)}
                      style={{ color: 'white' }}
                    >
                      1.0%
                    </div>
                  </div>
                </div>
              </div>

              {/* Removed Gas Price options as all transactions now use optimal V3 fees (~$0.32) */}

              <div className={classes.advancedSettings}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={transactionDeadline === 30}
                      onChange={() => setTransactionDeadline(transactionDeadline === 20 ? 30 : 20)}
                      color="primary"
                    />
                  }
                  label="Transaction deadline: 30 minutes"
                  style={{ color: 'white' }}
                />
                {/* Removed Uniswap version toggle since we always use V3 */}

              </div>
            </div>

          )}
          {/* Transaction Info */}
          <div className={classes.swapInfo}>
            <div className={classes.infoRow}>
              <Typography variant="body2">
                Rate
                <Tooltip title={`Exchange rate between ETH and ${isCurrentToken && tokenName ? tokenName : 'E3D'}`}>
                  <IconButton size="small" className={classes.infoIcon}>
                    <FaInfoCircle />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Typography variant="body2">
                1 ETH = {isCurrentToken && currentTokenPrice ? (ethPrice / currentTokenPrice).toLocaleString() : (ethPrice / e3dPrice).toLocaleString()} {isCurrentToken && tokenName ? tokenName : 'E3D'}
              </Typography>
            </div>
            <div className={classes.infoRow}>
              <Typography variant="body2">
                Slippage Tolerance
              </Typography>
              <Typography variant="body2">
                {slippage}%
              </Typography>
            </div>
            <div className={classes.infoRow}>
              <Typography variant="body2">
                Minimum received
              </Typography>
              <Typography variant="body2">
                {(parseFloat(tokenAmount) * (1 - slippage/100)).toFixed(2)} {isCurrentToken && tokenName ? tokenName : 'E3D'}
              </Typography>
            </div>
          </div>

          {/* Messages */}
          {errorMessage && (
            <div>
              {errorMessage.includes("Transaction submitted") ? (
                <div>
                  <Typography variant="body2" style={{ color: '#FFA500', marginTop: 16, textAlign: 'center' }}>
                    {errorMessage}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#aaaaaa', marginTop: 8, fontSize: '0.9rem', textAlign: 'center' }}>
                    The transaction may succeed or fail depending on network conditions.
                    Please check your wallet for the final status.
                  </Typography>
                </div>
              ) : (
                <Typography variant="body2" style={{ color: '#ff6961', marginTop: 16, textAlign: 'center' }}>
                  {errorMessage}
                </Typography>
              )}
            </div>
          )}

          {/* Success Message */}
          {swapSuccess && (
            <Typography variant="body2" style={{ color: '#77dd77', marginTop: 16, textAlign: 'center' }}>
              Swap completed successfully!
            </Typography>
          )}

          {/* Swap Button */}
          <Button
            className={classes.swapButton}
            style={{ marginTop: 24 }}
            onClick={executeSwap}
            disabled={!walletAddress || isLoading || parseFloat(ethAmount) <= 0 || !selectedToken || parseFloat(ethAmount) > parseFloat(selectedToken?.balance || '0')}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : !walletAddress ? (
              "Connect Wallet"
            ) : parseFloat(ethAmount) <= 0 ? (
              "Enter an amount"
            ) : !selectedToken ? (
              "Select a token"
            ) : parseFloat(ethAmount) > parseFloat(selectedToken.balance) ? (
              `Insufficient ${selectedToken.symbol} balance`
            ) : (
              "Swap"
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  // Debug what's happening with the dialog mode
  console.log('SwapPage render state:', { open, isDialog, walletAddress });
  
  // Check if walletAddress from props is null or undefined but we have a provider
  useEffect(() => {
    if ((!walletAddress || walletAddress === null) && provider) {
      const getAddress = async () => {
        try {
          const accounts = await provider.listAccounts();
          if (accounts && accounts.length > 0) {
            setWalletAddress(accounts[0]);
            console.log('SwapPage: Set wallet address from provider:', accounts[0]);
          }
        } catch (error) {
          console.error('SwapPage: Error getting wallet address from provider', error);
        }
      };
      getAddress();
    }
  }, [provider, walletAddress]);

  // Don't add any global style overrides that might break the dialog structure

  // Conditional rendering based on whether it's used as a dialog or standalone page
  return isDialog ? (
    <Dialog 
      open={open === null ? false : open} 
      onClose={onClose} 
      maxWidth="sm"
      scroll="body"
      fullWidth={false}
      style={{ zIndex: 8888 }}
      PaperProps={{
        style: {
          border: '1px solid #4d79ff',
          boxShadow: '0 0 0 1px #4d79ff',
          borderRadius: '15px',
          overflow: 'visible',
          maxWidth: '450px',
          margin: '0 auto',
          boxSizing: 'border-box',
          backgroundColor: '#1f2026',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch'
        }
      }}
      BackdropProps={{
        style: { backgroundColor: 'rgba(0,0,0,0.7)' }
      }}
    >
      <DialogTitle style={{ backgroundColor: '#1f2026', color: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {isCurrentToken ? `Swap ETH to ${tokenName}` : 'Swap Tokens'}
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent style={{ backgroundColor: '#1f2026', padding: 0, height: 'auto', overflow: 'visible' }}>
        {swapContent}
      </DialogContent>
    </Dialog>
  ) : swapContent;
};

export default SwapPage;
